import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { HandHeart, Loader2, TriangleAlert } from 'lucide-react';
import { inputClass, FieldError } from '../components/fields';
import { AMOUNT_LABEL, PRESETS_ORE, MAX_ORE } from '../components/donations/presets';
import { createDonation, recurringAvailable, startRecurring, formatOre, parseSekToOre } from '../lib/donations';

type Phase = 'idle' | 'submitting';
type Cadence = 'once' | 'monthly';

/**
 * Spec 007 + 007c — the public donation page (§7). No login: anyone may give. A donor picks a cadence
 * (one-time or monthly), an amount (preset or custom SEK), optionally leaves a name + email for a receipt
 * / a manage link, and is sent to the provider's hosted checkout. Four states: idle form → submitting
 * (loading) → a specific field/unavailable error → redirect on success. Copy anchors on §7: a gift buys
 * thanks, never standing. `?cancelled=1` returns from an abandoned checkout and shows a gentle notice
 * (never an error). The monthly option is shown only when the bound provider supports recurring (Stripe).
 */
export default function Donate() {
  const [params] = useSearchParams();
  const cancelled = params.get('cancelled') === '1';

  const [cadence, setCadence] = useState<Cadence>('once');
  // null = still checking; true/false once the provider capability is known.
  const [monthlyAvailable, setMonthlyAvailable] = useState<boolean | null>(null);
  const [amountInput, setAmountInput] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phase, setPhase] = useState<Phase>('idle');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    void recurringAvailable().then((r) => {
      if (!live) return;
      const ok = r.kind === 'ok' && r.data.available;
      setMonthlyAvailable(ok);
      if (!ok) setCadence('once'); // never leave the donor stuck on an unavailable cadence
    });
    return () => {
      live = false;
    };
  }, []);

  const amountOre = useMemo(() => parseSekToOre(amountInput), [amountInput]);
  const busy = phase === 'submitting';
  const monthly = cadence === 'monthly';

  function pickPreset(ore: number) {
    setAmountInput((ore / 100).toString());
    setErrors((e) => ({ ...e, amountOre: '' }));
  }

  function validate(): boolean {
    const next: Record<string, string> = {};
    if (amountOre === null) next.amountOre = 'Enter an amount in EUR, like 10 or 25,50.';
    else if (amountOre < 1) next.amountOre = 'The amount must be at least 0,01 €.';
    else if (amountOre > MAX_ORE) next.amountOre = 'That amount is above the maximum of €1 000 000.';
    if (email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      next.donorEmail = 'Enter a valid email, or leave it blank.';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    if (!validate() || amountOre === null) return;
    setPhase('submitting');
    const input = {
      amountOre,
      donorName: name.trim() || undefined,
      donorEmail: email.trim() || undefined,
    };
    const r = await (monthly ? startRecurring(input) : createDonation(input));
    if (r.kind === 'ok') {
      // Hand off to whichever provider is active: Stripe hosted checkout (→ /donate/thanks) or the Swish
      // QR page (→ /donate/swish/:ref). The frontend is provider-agnostic — it just follows redirectUrl.
      window.location.assign(r.data.redirectUrl);
      return;
    }
    setPhase('idle');
    if (r.kind === 'invalid') {
      setErrors(r.errors);
      if (!Object.keys(r.errors).length && r.message) setFormError(r.message);
    } else if (r.kind === 'unavailable') {
      setFormError(r.message);
    } else {
      setFormError('Something went wrong starting your donation. Please try again.');
    }
  }

  return (
    <div className="mx-auto w-full max-w-xl px-6 py-16">
      <header className="text-center">
        <HandHeart aria-hidden className="accent-glow mx-auto h-9 w-9 text-accent" />
        <h1 className="mt-4 font-display text-3xl font-semibold uppercase tracking-[0.12em] text-ink">
          Support the order
        </h1>
        <p className="mx-auto mt-3 max-w-md font-serif text-lg leading-relaxed text-muted">
          A gift, not a membership. It keeps the association running on a cost-recovery basis — and buys
          nothing but our thanks. No tiers, no perks, no standing (§7).
        </p>
      </header>

      {cancelled && (
        <p
          role="status"
          className="mt-6 rounded-md border border-border bg-surface px-3 py-2 font-sans text-sm text-muted"
        >
          Your payment was cancelled — nothing was charged. You’re welcome to try again whenever you like.
        </p>
      )}

      <form onSubmit={onSubmit} noValidate className="mt-8 space-y-6" aria-busy={busy}>
        {monthlyAvailable && (
          <fieldset className="space-y-3" disabled={busy}>
            <legend className="font-sans text-sm font-medium text-ink">How often</legend>
            <div role="radiogroup" aria-label="Donation frequency" className="flex gap-2">
              {(
                [
                  { value: 'once', label: 'One time' },
                  { value: 'monthly', label: 'Monthly' },
                ] as const
              ).map(({ value, label }) => {
                const active = cadence === value;
                return (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => setCadence(value)}
                    className={`flex-1 rounded-md border px-3 py-2 font-sans text-sm transition-colors ${
                      active
                        ? 'border-accent bg-accent/10 text-ink'
                        : 'border-border bg-surface text-muted hover:text-ink'
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            {monthly && (
              <p className="font-sans text-xs text-muted">
                Charged every month until you stop it. Leave an email below and we’ll send you a link to
                cancel anytime — no account needed.
              </p>
            )}
          </fieldset>
        )}

        <fieldset className="space-y-3" disabled={busy}>
          <legend className="font-sans text-sm font-medium text-ink">
            {monthly ? 'Monthly amount' : AMOUNT_LABEL}
          </legend>
          <div className="flex flex-wrap gap-2">
            {PRESETS_ORE.map((ore) => {
              const active = amountOre === ore;
              return (
                <button
                  key={ore}
                  type="button"
                  onClick={() => pickPreset(ore)}
                  aria-pressed={active}
                  className={`rounded-md border px-3 py-1.5 font-sans text-sm transition-colors ${
                    active
                      ? 'border-accent bg-accent/10 text-ink'
                      : 'border-border bg-surface text-muted hover:text-ink'
                  }`}
                >
                  {formatOre(ore)}
                </button>
              );
            })}
          </div>
          <div className="space-y-1.5">
            <label htmlFor="amount" className="sr-only">
              Amount in EUR
            </label>
            <div className="relative">
              <input
                id="amount"
                inputMode="decimal"
                autoComplete="off"
                placeholder="Other amount"
                value={amountInput}
                onChange={(e) => setAmountInput(e.target.value)}
                aria-invalid={!!errors.amountOre}
                aria-describedby={errors.amountOre ? 'amount-error' : undefined}
                className={`${inputClass} pr-12`}
              />
              <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center font-sans text-sm text-muted">
                €
              </span>
            </div>
            <FieldError id="amount-error" error={errors.amountOre} />
          </div>
        </fieldset>

        <fieldset className="space-y-3" disabled={busy}>
          <legend className="font-sans text-sm font-medium text-ink">
            Your details <span className="font-normal text-muted">— optional, for a receipt</span>
          </legend>
          <div className="space-y-1.5">
            <label htmlFor="donorName" className="font-sans text-xs text-muted">
              Name
            </label>
            <input
              id="donorName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
              autoComplete="name"
              placeholder="Leave blank to give anonymously"
              className={inputClass}
            />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="donorEmail" className="font-sans text-xs text-muted">
              Email
            </label>
            <input
              id="donorEmail"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              maxLength={254}
              autoComplete="email"
              placeholder="For a thank-you receipt"
              aria-invalid={!!errors.donorEmail}
              aria-describedby={errors.donorEmail ? 'email-error' : undefined}
              className={inputClass}
            />
            <FieldError id="email-error" error={errors.donorEmail} />
          </div>
        </fieldset>

        {formError && (
          <p
            role="alert"
            className="flex items-start gap-2 rounded-md border border-error/40 bg-error/5 px-3 py-2 font-sans text-sm text-error"
          >
            <TriangleAlert aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
            {formError}
          </p>
        )}

        <button
          type="submit"
          disabled={busy}
          className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-accent px-4 py-2.5 font-sans text-sm font-medium text-bg transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-60"
        >
          {busy ? (
            <>
              <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
              Taking you to checkout…
            </>
          ) : monthly ? (
            <>Set up monthly gift</>
          ) : (
            <>Continue to secure checkout</>
          )}
        </button>
        <p className="text-center font-sans text-xs text-muted">
          Payments are handled securely off-site by card. We never store your payment details.
        </p>
      </form>
    </div>
  );
}
